import { useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { BoardPoint, BoardSize } from '@wx-weiqi/contracts';
import { DrawingPointerSession, guardDrawingTouch } from '../services/drawing-input';

import {
  BOARD_PADDING,
  BOARD_VIEW_SIZE,
  boardCoordinate,
  type BoardViewport,
} from '../domain/board-geometry';

export type ReviewAnnotationTool = 'BRUSH' | 'LETTER' | 'TRIANGLE' | 'CIRCLE' | 'CROSS';
export type ReviewAnnotationColor = 'RED' | 'BLUE';

export interface ReviewBrushStroke {
  readonly kind: 'BRUSH';
  readonly color: ReviewAnnotationColor;
  readonly width: number;
  readonly points: readonly { readonly x: number; readonly y: number }[];
}

export interface ReviewPointAnnotation {
  readonly kind: Exclude<ReviewAnnotationTool, 'BRUSH'>;
  readonly color: ReviewAnnotationColor;
  readonly point: BoardPoint;
  readonly label: string | null;
}

export type ReviewAnnotation = ReviewBrushStroke | ReviewPointAnnotation;

interface ReviewAnnotationLayerProps {
  readonly size: BoardSize;
  readonly open: boolean;
  readonly tool: ReviewAnnotationTool;
  readonly color: ReviewAnnotationColor;
  readonly width: number;
  readonly annotations: readonly ReviewAnnotation[];
  readonly viewport?: BoardViewport | null;
  readonly onStroke: (stroke: ReviewBrushStroke) => void;
  readonly onPoint: (point: BoardPoint) => void;
}

const COLORS: Readonly<Record<ReviewAnnotationColor, string>> = {
  RED: '#d43a31',
  BLUE: '#1768d7',
};

export interface ReviewPointerBounds {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export function reviewAnnotationCoordinate(
  clientX: number,
  clientY: number,
  bounds: ReviewPointerBounds,
  viewport: BoardViewport | null,
): { readonly x: number; readonly y: number } {
  const view = viewport ?? {
    x: 0,
    y: 0,
    width: BOARD_VIEW_SIZE,
    height: BOARD_VIEW_SIZE,
  };
  const width = Math.max(1, bounds.width);
  const height = Math.max(1, bounds.height);
  return {
    x: view.x + ((clientX - bounds.left) / width) * view.width,
    y: view.y + ((clientY - bounds.top) / height) * view.height,
  };
}

export function reviewAnnotationPoint(
  size: BoardSize,
  clientX: number,
  clientY: number,
  bounds: ReviewPointerBounds,
  viewport: BoardViewport | null,
): BoardPoint {
  const value = reviewAnnotationCoordinate(clientX, clientY, bounds, viewport);
  const spacing = (BOARD_VIEW_SIZE - BOARD_PADDING * 2) / (size - 1);
  return {
    x: Math.max(0, Math.min(size - 1, Math.round((value.x - BOARD_PADDING) / spacing))),
    y: Math.max(0, Math.min(size - 1, Math.round((value.y - BOARD_PADDING) / spacing))),
  };
}

function symbol(annotation: ReviewPointAnnotation): string {
  if (annotation.kind === 'LETTER') return annotation.label ?? 'A';
  if (annotation.kind === 'TRIANGLE') return '△';
  if (annotation.kind === 'CIRCLE') return '〇';
  return '×';
}

export function nextReviewLetter(index: number): string {
  let value = Math.max(0, Math.trunc(index));
  let label = '';
  do {
    label = String.fromCharCode(65 + (value % 26)) + label;
    value = Math.floor(value / 26) - 1;
  } while (value >= 0);
  return label;
}

export function toggleReviewPointAnnotation(
  annotations: readonly ReviewAnnotation[],
  point: BoardPoint,
  tool: Exclude<ReviewAnnotationTool, 'BRUSH'>,
  color: ReviewAnnotationColor,
): readonly ReviewAnnotation[] {
  const existing = annotations.some(
    (annotation) =>
      annotation.kind !== 'BRUSH' &&
      annotation.point.x === point.x &&
      annotation.point.y === point.y,
  );
  if (existing) {
    return annotations.filter(
      (annotation) =>
        annotation.kind === 'BRUSH' ||
        annotation.point.x !== point.x ||
        annotation.point.y !== point.y,
    );
  }
  const usedLetters = new Set(
    annotations.flatMap((annotation) =>
      annotation.kind === 'LETTER' && annotation.label !== null ? [annotation.label] : [],
    ),
  );
  let letterIndex = 0;
  while (usedLetters.has(nextReviewLetter(letterIndex))) letterIndex += 1;
  const label = tool === 'LETTER' ? nextReviewLetter(letterIndex) : null;
  return [...annotations, { kind: tool, color, point, label }];
}

export function ReviewAnnotationLayer({
  size,
  open,
  tool,
  color,
  width,
  annotations,
  viewport = null,
  onStroke,
  onPoint,
}: ReviewAnnotationLayerProps) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const session = useRef(new DrawingPointerSession());
  const [draftPoints, setDraftPoints] = useState<readonly { x: number; y: number }[]>([]);
  useLayoutEffect(() => {
    session.current.cancel();
    setDraftPoints([]);
    if (!open || surfaceRef.current === null) return;
    return guardDrawingTouch(surfaceRef.current);
  }, [open, tool]);

  const coordinate = (event: ReactPointerEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return reviewAnnotationCoordinate(event.clientX, event.clientY, bounds, viewport);
  };

  const pointFromEvent = (event: ReactPointerEvent<HTMLDivElement>): BoardPoint => {
    return reviewAnnotationPoint(
      size,
      event.clientX,
      event.clientY,
      event.currentTarget.getBoundingClientRect(),
      viewport,
    );
  };

  const begin = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!open || !event.isPrimary || event.button !== 0) return;
    event.preventDefault();
    if (tool !== 'BRUSH') {
      onPoint(pointFromEvent(event));
      return;
    }
    const points = session.current.begin(event, coordinate(event));
    if (points === null) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setDraftPoints(points);
  };

  const move = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!open || tool !== 'BRUSH') return;
    const points = session.current.move(event.pointerId, coordinate(event));
    if (points === null) return;
    event.preventDefault();
    setDraftPoints(points);
  };

  const finish = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!open || tool !== 'BRUSH') return;
    const points = session.current.finish(event.pointerId, coordinate(event));
    if (points === null) return;
    event.preventDefault();
    setDraftPoints([]);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (points.length > 1) onStroke({ kind: 'BRUSH', color, width, points });
  };
  const cancel = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (session.current.cancel(event.pointerId)) setDraftPoints([]);
  };

  return (
    <div
      ref={surfaceRef}
      className={`review-annotation-surface${open ? ' is-editing' : ''}`}
      onPointerDown={begin}
      onPointerMove={move}
      onPointerUp={finish}
      onPointerCancel={cancel}
      onLostPointerCapture={cancel}
      onContextMenu={(event) => {
        if (!open) return;
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <svg
        className={`review-annotation-layer${open ? ' is-editing' : ''}`}
        viewBox={
          viewport === null
            ? `0 0 ${BOARD_VIEW_SIZE} ${BOARD_VIEW_SIZE}`
            : `${viewport.x} ${viewport.y} ${viewport.width} ${viewport.height}`
        }
        aria-label={open ? '棋盘标记绘制层' : undefined}
        aria-hidden={open ? undefined : true}
      >
        {annotations.map((annotation, index) =>
          annotation.kind === 'BRUSH' ? (
            <polyline
              key={`brush-${index}`}
              points={annotation.points.map((point) => `${point.x},${point.y}`).join(' ')}
              fill="none"
              stroke={COLORS[annotation.color]}
              strokeWidth={annotation.width}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : (
            <text
              key={`mark-${index}-${annotation.point.x}-${annotation.point.y}`}
              x={boardCoordinate(size, annotation.point.x)}
              y={boardCoordinate(size, annotation.point.y)}
              className="review-point-annotation"
              fill={COLORS[annotation.color]}
              textAnchor="middle"
              dominantBaseline="central"
            >
              {symbol(annotation)}
            </text>
          ),
        )}
        {draftPoints.length > 1 ? (
          <polyline
            points={draftPoints.map((point) => `${point.x},${point.y}`).join(' ')}
            fill="none"
            stroke={COLORS[color]}
            strokeWidth={width}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : null}
      </svg>
    </div>
  );
}
